import { area as d3Area, line as d3Line } from 'd3-shape';

import {
  DEFAULT_DATA_POINT_RADIUS,
  DEFAULT_FILL_OPACITY,
  DEFAULT_SERIES_CURVE_SHAPE,
} from '../../../../constants.js';
import { OVERLAY_PADDING } from '../../../data-point/constants.js';
import {
  AreaCanvasInstance,
  FnCanvasAccessor,
} from '../../types/canvas-shape.js';
import { getCanvasColor } from '../canvas-color.js';
import { mapSeriesCurveToD3 } from '../map-series-curve-to-d3.js';

type FnAreaCanvasStyle<Datum> = (
  ctx: CanvasRenderingContext2D,
  areaCanvas: AreaCanvasInstance<Datum>,
  x: FnCanvasAccessor<Datum>,
  y0: FnCanvasAccessor<Datum>,
  y1: FnCanvasAccessor<Datum>,
) => void;

/**
 * Builds style FN Area based on parameters.
 *
 * @param strokeWidth - stroke width
 * @param radius - scatter plot radius
 * @param lineStrokeWidth - upper line stroke width
 * @param enableDotOverflow - enable dots indicators to be drawn overflowing the plot area
 * @returns - fn print Area canvas
 */
export function printAreaBuilder<Datum>(
  strokeWidth: number,
  radius: number = DEFAULT_DATA_POINT_RADIUS,
  lineStrokeWidth: number,
  enableDotOverflow: boolean,
): FnAreaCanvasStyle<Datum> {
  const area = d3Area<Datum>();
  const line = d3Line<Datum>();

  return (ctx, areaCanvas, x, y0, y1) => {
    const {
      color,
      strokeColor,
      lineColor,
      data,
      opacity = DEFAULT_FILL_OPACITY,
      defined = () => true,
      displayScatterPlot,
      curve = DEFAULT_SERIES_CURVE_SHAPE,
    } = areaCanvas;

    const [firstDatum] = data;
    const computedColor = color(firstDatum);
    const isGradient = typeof computedColor !== 'string';
    const datumColor = !isGradient ? getCanvasColor(computedColor) : undefined;
    const gradientColor = isGradient ? computedColor : undefined;
    const datumStrokeColor =
      strokeColor && getCanvasColor(strokeColor(firstDatum));
    const lineStrokeColor = lineColor && getCanvasColor(lineColor(firstDatum));
    const linesCurve = mapSeriesCurveToD3(curve);
    const hasToPrintScatter = data.length && displayScatterPlot?.(data, 0);

    ctx.save();

    if (enableDotOverflow) {
      ctx.translate(OVERLAY_PADDING / 2, OVERLAY_PADDING / 2);
      ctx.save();

      ctx.beginPath();
      ctx.rect(0, 0, areaCanvas.canvasWidth, areaCanvas.canvasHeight);
      ctx.clip();
    }
    // Shared styles
    ctx.filter = 'none';
    ctx.globalAlpha = opacity;

    // Draw area
    ctx.beginPath();
    ctx.lineJoin = 'bevel';
    const areaPath = area
      .defined(defined)
      .curve(linesCurve)
      .x(x)
      .y0(y0)
      .y1(y1)
      .context(ctx);
    areaPath(data);

    if (data.length) {
      if (datumStrokeColor || datumColor) {
        // Style area border
        ctx.strokeStyle = (datumStrokeColor || datumColor) ?? '';
        ctx.lineWidth = strokeWidth;

        ctx.stroke();
      }
    }

    // Fill area with a color o a gradient
    if (datumColor) {
      ctx.fillStyle = datumColor;
      ctx.fill();
    } else if (gradientColor) {
      gradientColor({ context: ctx });
    }

    if (enableDotOverflow) {
      ctx.restore();
    }

    if (hasToPrintScatter) {
      ctx.beginPath();
      const dataLength = data.length;
      const scatterRadius = radius ?? DEFAULT_DATA_POINT_RADIUS;

      for (let i = 0; i < dataLength; i++) {
        if (
          y1(data[i], i) > areaCanvas.canvasHeight ||
          y1(data[i], i) < 0 ||
          x(data[i], i) > areaCanvas.canvasWidth ||
          x(data[i], i) < 0
        ) {
          continue;
        }

        ctx.moveTo(x(data[i], i), y1(data[i], i));
        ctx.arc(x(data[i], i), y1(data[i], i), scatterRadius, 0, 2 * Math.PI);
      }

      if (datumColor) {
        ctx.fillStyle = datumColor;
      }

      ctx.filter = 'none';
      ctx.globalAlpha = opacity;
      ctx.fill();
    }

    // Style sparkline line
    if (lineStrokeColor) {
      ctx.beginPath();

      ctx.strokeStyle = lineStrokeColor;
      ctx.lineWidth = lineStrokeWidth;
      const linePath = line
        .defined(defined)
        .curve(linesCurve)
        .x(x)
        .y(y1)
        .context(ctx);
      linePath(data);

      ctx.stroke();
    }

    ctx.restore();
  };
}
